import { useCallback, useMemo } from 'react';
import { usePaginatedList } from 'hooks/use-paginated-list';

export const usePaginatedServices = ({ servicesApi, enabled = true } = {}) => {
  const fetchPage = useCallback(
    ({ page, per }) =>
      servicesApi.list({
        params: {
          page,
          per,
        },
      }),
    [servicesApi],
  );

  const getItems = useCallback((data) => data.services, []);

  const {
    items,
    loadMore,
    hasNextPage,
    inProcess,
    isLoadingMore,
    isLoading,
    pagination,
    reset,
  } = usePaginatedList({
    fetchPage,
    getItems,
    enabled: Boolean(enabled && servicesApi),
    resetDeps: [servicesApi],
  });

  return {
    services: items,
    loadMore,
    hasNextPage,
    inProcess,
    isLoadingMore,
    isLoading,
    pagination,
    reset,
  };
};

export const usePaginatedRestaurants = ({
  restaurantsApi,
  companyId,
  enabled = true,
} = {}) => {
  const fetchPage = useCallback(
    ({ page, per }) =>
      restaurantsApi.list({
        params: {
          page,
          per,
          ...(companyId ? { company_id: companyId } : {}),
        },
      }),
    [restaurantsApi, companyId],
  );

  const getItems = useCallback((data) => data.restaurants, []);

  const {
    items,
    loadMore,
    hasNextPage,
    inProcess,
    isLoadingMore,
    isLoading,
    pagination,
    reset,
  } = usePaginatedList({
    fetchPage,
    getItems,
    enabled: Boolean(enabled && restaurantsApi && companyId),
    resetDeps: [restaurantsApi, companyId],
  });

  return {
    restaurants: items,
    loadMore,
    hasNextPage,
    inProcess,
    isLoadingMore,
    isLoading,
    pagination,
    reset,
  };
};

export const usePaginatedDishes = ({
  dishesApi,
  categoryId,
  enabled = true,
} = {}) => {
  const fetchPage = useCallback(
    ({ page, per }) =>
      dishesApi.list({
        params: {
          page,
          per,
          ...(categoryId ? { category_id: categoryId } : {}),
        },
      }),
    [dishesApi, categoryId],
  );

  const getItems = useCallback((data) => data.dishes, []);

  const {
    items,
    loadMore,
    hasNextPage,
    inProcess,
    isLoadingMore,
    isLoading,
    pagination,
    reset,
  } = usePaginatedList({
    fetchPage,
    getItems,
    enabled: Boolean(enabled && dishesApi),
    resetDeps: [dishesApi, categoryId],
  });

  return {
    dishes: items,
    loadMore,
    hasNextPage,
    inProcess,
    isLoadingMore,
    isLoading,
    pagination,
    reset,
  };
};
